/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { store } from 'redux/store';

import LoadingRound from '@components/loading-round/loading-round';

import { initWebWorker } from 'webworker';

import { bookmarksActions } from '@redux/slices/bookmarks';

import style from './home.module.scss';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { getLatestCoordinates } from '@utilities/get-distance';
import { cacheActions } from '@redux/slices/cache';
import DisabledLocation from '@components/location-toast';
import useNetworkStatus from '@hooks/useNetworkStatus';
import { checkIfTheLocationIsNeedToUpdateOrNot } from '@utilities/date-filters';
import { settingsActions } from '@redux/slices/settings';

const Glance = () => {
  const dispatch = useDispatch();
  const [isOnline] = useNetworkStatus();
  const { cachingComplete: isCachingComplete, refreshingAndSyncingData } = useSelector(
    (state) => state.cache
  );
  const { coordinates, unit, locationUpdateTime } = useSelector((state) => state.cache);

  const {
    bookmarks = [],
    bookmarkValidity,
    bookmarksWithCount
  } = useSelector((state) => state.bookmarks);
  const [bookmarkList, setBookmarkList] = useState([]);
  const [showLocationDisabledInfo, setShowLocationDisabledInfo] = useState(false);
  const [loading, setLoading] = useState(!refreshingAndSyncingData);
  const dataFetchedRef = useRef(false);
  const { width } = useWindowDimensions();

  const atAGlanceBookmarkList = bookmarks?.filter((book) => book?.atAGlance);

  if (atAGlanceBookmarkList?.length !== bookmarksWithCount?.length) {
    dispatch?.(bookmarksActions.setBookmarkValidity(true));
  }

  const glanceLink = (bookmark) => {
    const name = bookmark?.name;
    if (
      bookmark?.type === 'Site Details' ||
      bookmark?.type === 'Parent Site Details' ||
      bookmark?.type === 'Equipment Details' ||
      bookmark?.type === 'Structure Details'
    )
      return `${bookmark?.listing}?bookmarkName=${name}&bookmarkId=${bookmark.bookmarkId}` || '/';
    else {
      let urlString = bookmark?.listing;
      const queryString = urlString?.split('?')[1];

      let redirectURL =
        `${bookmark?.listing}${queryString ? '&' : '?'}bookmarkFilter=${
          bookmark?.filters
        }&bookmarkName=${
          bookmark?.name
        }${`&bookmarkTab=${bookmark?.tab}`}${`&bookmarkKeyName=${bookmark?.keyName}`}${`&currentOpenTaskTab=${bookmark?.currentOpenTaskTab}`}&bookmarkId=${
          bookmark.bookmarkId
        }` || '/';

      redirectURL =
        bookmark?.type === 'Sites' && bookmark?.showMapView
          ? `${redirectURL}&showMapView=${
              bookmark?.showMapView || false
            }&selectedSiteIdForMapView=${
              bookmark?.selectedSiteIdForMapView
                ? JSON.stringify(bookmark?.selectedSiteIdForMapView)
                : null
            }`
          : redirectURL;

      redirectURL =
        bookmark?.type === 'Sites' && bookmark?.siteTypeQuery
          ? `${redirectURL}&type=${bookmark?.siteTypeQuery}&filterType=${bookmark?.siteTypeQuery}`
          : redirectURL;

      return redirectURL;
    }
  };

  useEffect(() => {
    if (!refreshingAndSyncingData) {
      if (isCachingComplete || !dataFetchedRef.current) {
        setLoading(true);
        const checkAllBookmarksIsNotAtAGlance = bookmarks?.filter((book) => book?.atAGlance);
        if (_.size(checkAllBookmarksIsNotAtAGlance) > 0) {
          try {
            if (bookmarkValidity) {
              const updatedAndNewBookmarks = bookmarks?.filter(
                (book) => !book?.sync || book?.update
              );
              if (updatedAndNewBookmarks?.length > 0) {
                dispatch?.(settingsActions.addOrUpdateNewAndExistingBookmark());
              }

              checkIfTheLocationIsNeedToUpdateOrNot({
                addNewCoordinates: locationUpdateTime ? false : true,
                loadingFunctionCall: () => {},
                isOnline: navigator.onLine,
                unit,
                locationUpdateTime,
                oldCoordinates: coordinates,
                functionCall: getAllSiteListingFromSelector,
                params: {
                  coordinates,
                  checkAllBookmarksIsNotAtAGlance
                }
              });
            } else {
              setBookmarkList(bookmarksWithCount);
              setLoading(false);
            }
          } catch (e) {
            console.log({ e });
          }
        } else setLoading(false);
      }
    }
  }, [
    isCachingComplete,
    bookmarks,
    bookmarkValidity,
    bookmarksWithCount,
    dispatch,
    locationUpdateTime,
    coordinates,
    refreshingAndSyncingData
  ]);

  const getAllSiteListingFromSelector = ({ coordinates, checkAllBookmarksIsNotAtAGlance }) => {
    initWebWorker({
      type: 'get-all-default-bookmarks',
      args: {
        store: { ...store.getState() },
        checkAllBookmarksIsNotAtAGlance
      },
      timeout: 180000
    })
      .then((data) => {
        const list = data?.result?.sort((a, b) => b?.atAGlanceOrder - a?.atAGlanceOrder) || [];
        dispatch?.(bookmarksActions.setBookmarksWithCount(list));
        dispatch?.(bookmarksActions.setBookmarkValidity(false));
        setBookmarkList(list);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const glanceBookmarksWidth = useMemo(() => {
    if (width <= 430) {
      return Math.round((width - 64) / 3) - 1;
    }
    return null;
  }, [width]);

  return (
    <>
      <DisabledLocation
        showLocationDisabledInfo={showLocationDisabledInfo}
        text={'Map view is not available offline.'}
        className={style.mapNotification}
      />

      {/* <button
        onClick={() => {
          dispatch?.(bookmarksActions.setBookmarkValidity(true));
        }}
      >
        CLick me
      </button> */}

      {loading ? (
        <div style={{ maxWidth: '768px' }}>
          <LoadingRound className={style.glanceLoaderHight} />
        </div>
      ) : (
        <>
          <label
            className={`${style['label-capital-semibold']} ${style['primary-800']} mx-4 md:mx-8  block`}
          >
            At a Glance{' '}
          </label>

          <div
            id="at-a-glance"
            className={`${style['op-glance-wrap']} mt-4 pb-24 md:pb-16 md:pl-8 md:pr-24 ${style.myClassScroll}`}
          >
            <div
              className={`${style['op-glance-row']} pb-4 md:pb-0 flex flex-nowrap  gap-4 md:flex-wrap ${style.noWrapOnSmallScreen} `}
            >
              {bookmarkList?.map(
                (list, index) =>
                  list?.atAGlance && (
                    <div className="" key={list?.bookmarkId || index}>
                      <Link
                        to={glanceLink(list)}
                        onClick={
                          list?.type === 'Sites' && !isOnline && list?.showMapView
                            ? (e) => {
                                e.preventDefault();
                                if (list?.showMapView && !isOnline) {
                                  setShowLocationDisabledInfo(true);
                                  setTimeout(() => {
                                    setShowLocationDisabledInfo(false);
                                  }, 3000);
                                }
                              }
                            : () => {}
                        }
                      >
                        <div
                          className={style.box}
                          style={{
                            width: glanceBookmarksWidth ? `${glanceBookmarksWidth}px` : '112px'
                          }}
                        >
                          <h1
                            style={{
                              width: glanceBookmarksWidth
                                ? `${glanceBookmarksWidth - 16 - 2}px`
                                : '96px'
                            }}
                          >
                            {list?.count > 999 ? '999+' : list?.count}
                          </h1>{' '}
                          <div className={style.ellipsis}>
                            <p
                              className={`${list?.name.length > 15 && style.ellipsisText}`}
                              style={{
                                width: glanceBookmarksWidth
                                  ? `${glanceBookmarksWidth - 16 - 2}px`
                                  : '96px'
                              }}
                            >
                              {list?.name}
                            </p>{' '}
                            {list?.name.length > 15 && (
                              <div className={style.tooltip}>
                                <p>{list?.name}</p>{' '}
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  )
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default React.memo(Glance);
